import { createEvent, createStore } from 'effector';

import type { OnboardingVariant } from '../deriveVariant';

const SESSION_KEY = 'onboarding_isReturningUser';
const FLOW_COMPLETE_KEY = 'onboarding_isFlowComplete';
const PENDING_SUBSCRIPTION_RESUME_KEY = 'onboarding_pendingSubscriptionResume';
const STP_FLOW_KEY = 'onboarding_isStpFlow';

export type SubscriptionOnboardingVariant = Extract<
  OnboardingVariant,
  'enterpriseSub' | 'individualSub'
>;

export type PendingSubscriptionResume = {
  search: string;
  variant: SubscriptionOnboardingVariant;
};

function readStoredIsReturningUser(): boolean | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function writeStoredIsReturningUser(value: boolean | null) {
  try {
    if (value === null) sessionStorage.removeItem(SESSION_KEY);
    else sessionStorage.setItem(SESSION_KEY, String(value));
  } catch {
    // Ignore quota / private-mode failures — the store still works for the current page.
  }
}

function readStoredFlowComplete(): boolean {
  try {
    return sessionStorage.getItem(FLOW_COMPLETE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeStoredFlowComplete(value: boolean) {
  try {
    if (value) sessionStorage.setItem(FLOW_COMPLETE_KEY, 'true');
    else sessionStorage.removeItem(FLOW_COMPLETE_KEY);
  } catch {
    // Ignore quota / private-mode failures — the store still works for the current page.
  }
}

function readStoredPendingSubscriptionResume(): PendingSubscriptionResume | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SUBSCRIPTION_RESUME_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PendingSubscriptionResume>;
    if (
      typeof value.search !== 'string' ||
      (value.variant !== 'enterpriseSub' && value.variant !== 'individualSub')
    ) {
      return null;
    }
    return { search: value.search, variant: value.variant };
  } catch {
    return null;
  }
}

function writeStoredPendingSubscriptionResume(value: PendingSubscriptionResume | null) {
  try {
    if (value === null) sessionStorage.removeItem(PENDING_SUBSCRIPTION_RESUME_KEY);
    else sessionStorage.setItem(PENDING_SUBSCRIPTION_RESUME_KEY, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode failures — the store still works for the current page.
  }
}

function readStoredIsStpFlow(): boolean {
  try {
    return sessionStorage.getItem(STP_FLOW_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeStoredIsStpFlow(value: boolean) {
  try {
    if (value) sessionStorage.setItem(STP_FLOW_KEY, 'true');
    else sessionStorage.removeItem(STP_FLOW_KEY);
  } catch {
    // Ignore quota / private-mode failures — the store still works for the current page.
  }
}

/** Persists the verified account type across refreshes within the current onboarding attempt. */
export const setIsReturningUser = createEvent<boolean | null>();
export const setIsFlowComplete = createEvent<boolean>();
export const setIsStpFlow = createEvent<boolean>();
export const setPendingSubscriptionResume = createEvent<PendingSubscriptionResume | null>();
export const resetOnboardingSession = createEvent();

export const onboardingSessionStore = createStore<{
  isFlowComplete: boolean;
  isReturningUser: boolean | null;
  isStpFlow: boolean;
  pendingSubscriptionResume: PendingSubscriptionResume | null;
}>({
  isFlowComplete: readStoredFlowComplete(),
  isReturningUser: readStoredIsReturningUser(),
  isStpFlow: readStoredIsStpFlow(),
  pendingSubscriptionResume: readStoredPendingSubscriptionResume(),
})
  .on(setIsReturningUser, (state, isReturningUser) => ({ ...state, isReturningUser }))
  .on(setIsFlowComplete, (state, isFlowComplete) => ({ ...state, isFlowComplete }))
  .on(setIsStpFlow, (state, isStpFlow) => ({ ...state, isStpFlow }))
  .on(setPendingSubscriptionResume, (state, pendingSubscriptionResume) => ({
    ...state,
    pendingSubscriptionResume,
  }))
  .on(resetOnboardingSession, () => ({
    isFlowComplete: false,
    isReturningUser: null,
    isStpFlow: false,
    pendingSubscriptionResume: null,
  }));

setIsReturningUser.watch(writeStoredIsReturningUser);
setIsFlowComplete.watch(writeStoredFlowComplete);
setIsStpFlow.watch(writeStoredIsStpFlow);
setPendingSubscriptionResume.watch(writeStoredPendingSubscriptionResume);
resetOnboardingSession.watch(() => {
  writeStoredIsReturningUser(null);
  writeStoredFlowComplete(false);
  writeStoredIsStpFlow(false);
  writeStoredPendingSubscriptionResume(null);
});
